import { KioskSessionRepository } from '../data/kiosk-session-repository';
import { KioskSessionTransformer } from '../transformers/KioskSessionTransformer';
import { 
  KioskSessionFilters, 
  KioskSessionGridRow, 
  KioskSessionDetail,
  HierarchicalSessionRow,
  LocationGroupRow 
} from '../types/kiosk-session-types';

/**
 * Use Case for Kiosk Sessions operations
 * Orchestrates business logic between repository, transformers, and UI
 * Follows the clean architecture pattern established in the existing codebase
 */
export class GetKioskSessionsUseCase {
  constructor(
    private readonly repository: KioskSessionRepository
  ) {}

  /**
   * Retrieves and transforms kiosk sessions for grid display
   * Applies filters, validates data, and handles errors
   */
  async getSessionsForGrid(filters: KioskSessionFilters): Promise<{
    data: KioskSessionGridRow[];
    totalCount: number;
    hasError: boolean;
    errorMessage?: string;
  }> {
    try {
      // Check authentication
      const isAuthenticated = await this.repository.isAuthenticated();
      if (!isAuthenticated) {
        return {
          data: [],
          totalCount: 0,
          hasError: true,
          errorMessage: 'Authentication required. Please log in to view kiosk sessions.'
        };
      }

      // Make two parallel API calls:
      // 1. Get total count (count=true) - for pagination display
      // 2. Get data rows (count=false) - for grid data
      const [totalCount, response] = await Promise.all([
        this.repository.getSessionsCount(filters),
        this.repository.getSessionsList(filters)
      ]);

      // Transform API response to grid format
      let gridData = KioskSessionTransformer.transformToGridRows(response);

      // Apply client-side filters for search and last4Digits
      gridData = KioskSessionTransformer.applyClientSideFilters(gridData, filters);

      return {
        data: gridData,
        totalCount: totalCount, // Use count from first API call
        hasError: false
      };

    } catch (error) {
      console.error('GetKioskSessionsUseCase.getSessionsForGrid failed:', error);
      
      return {
        data: [],
        totalCount: 0,
        hasError: true,
        errorMessage: KioskSessionTransformer.transformApiError(error)
      };
    }
  }

  /**
   * Retrieves detailed information for a specific session
   * Used for the detail blade/panel functionality
   */
  async getSessionDetail(sessionId: string, operatorId?: string): Promise<{
    data: KioskSessionDetail | null;
    hasError: boolean;
    errorMessage?: string;
  }> {
    try {
      if (!sessionId) {
        return {
          data: null,
          hasError: true,
          errorMessage: 'Session ID is required'
        };
      }

      // Check authentication
      const isAuthenticated = await this.repository.isAuthenticated();
      if (!isAuthenticated) {
        return {
          data: null,
          hasError: true,
          errorMessage: 'Authentication required. Please log in to view session details.'
        };
      }

      // Fetch session detail from repository with optional operator ID
      const response = await this.repository.getSessionDetail(sessionId, operatorId);

      // Transform to detail format
      const detailData = KioskSessionTransformer.transformToSessionDetail(response);

      if (!detailData) {
        return {
          data: null,
          hasError: true,
          errorMessage: 'Session not found or invalid data received'
        };
      }

      return {
        data: detailData,
        hasError: false
      };

    } catch (error) {
      console.error(`GetKioskSessionsUseCase.getSessionDetail failed for ${sessionId}:`, error);
      
      return {
        data: null,
        hasError: true,
        errorMessage: KioskSessionTransformer.transformApiError(error)
      };
    }
  }

  /**
   * Gets ALL sessions grouped by OCPI location (no pagination)
   * Useful for the "group by location" toggle feature
   * Groups by location_name from session data — no actor hierarchy needed
   */
  async getSessionsGroupedByLocation(filters: KioskSessionFilters): Promise<{
    groupedData: Map<string, KioskSessionGridRow[]>;
    hierarchicalData: HierarchicalSessionRow[];
    totalCount: number;
    hasError: boolean;
    errorMessage?: string;
  }> {
    try {
      // Remove pagination limit to load all sessions; keep locationId if set
      const filtersWithoutLimit: KioskSessionFilters = {
        ...filters,
        limit: undefined,
        groupByLocation: true
      };

      // Get ALL sessions (no pagination)
      const sessionsResult = await this.getSessionsForGrid(filtersWithoutLimit);

      if (sessionsResult.hasError) {
        return {
          groupedData: new Map(),
          hierarchicalData: [],
          totalCount: 0,
          hasError: true,
          errorMessage: sessionsResult.errorMessage
        };
      }

      // Group sessions by OCPI location_name (already available on each session)
      const grouped = this.groupSessionsByLocation(sessionsResult.data);

      // Create hierarchical structure for TanStack Table using OCPI location data
      const hierarchical = this.createHierarchicalStructure(sessionsResult.data);

      return {
        groupedData: grouped,
        hierarchicalData: hierarchical,
        totalCount: sessionsResult.data.length,
        hasError: false
      };

    } catch (error) {
      console.error('GetKioskSessionsUseCase.getSessionsGroupedByLocation failed:', error);

      return {
        groupedData: new Map(),
        hierarchicalData: [],
        totalCount: 0,
        hasError: true,
        errorMessage: KioskSessionTransformer.transformApiError(error)
      };
    }
  }

  /**
   * Gets default filters for initial page load
   * Uses current week as default date range
   */
  getDefaultFilters(): KioskSessionFilters {
    return this.repository.getDefaultFilters();
  }

  /**
   * Validates filter parameters and provides user-friendly error messages
   */
  validateFilters(filters: KioskSessionFilters): {
    isValid: boolean;
    errorMessage?: string;
  } {
    try {
      // Basic validation for required fields
      if (!filters.dateFrom || !filters.dateTo) {
        throw new Error('Date range is required');
      }
      
      if (filters.dateFrom > filters.dateTo) {
        throw new Error('Start date must be before end date');
      }
      
      return { isValid: true };
    } catch (error) {
      return {
        isValid: false,
        errorMessage: error instanceof Error ? error.message : 'Invalid filter parameters'
      };
    }
  }

  /**
   * Tests system connectivity and authentication
   * Useful for troubleshooting and health checks
   */
  async testSystemHealth(): Promise<{
    isHealthy: boolean;
    details: {
      authentication: boolean;
      apiConnectivity: boolean;
    };
    errorMessage?: string;
  }> {
    try {
      const isAuthenticated = await this.repository.isAuthenticated();
      
      // Test API connectivity by making a simple request
      let canConnect = false;
      if (isAuthenticated) {
        try {
          const defaultFilters = this.repository.getDefaultFilters();
          await this.repository.getSessionsList(defaultFilters);
          canConnect = true;
        } catch {
          canConnect = false;
        }
      }

      return {
        isHealthy: isAuthenticated && canConnect,
        details: {
          authentication: isAuthenticated,
          apiConnectivity: canConnect
        }
      };

    } catch (error) {
      return {
        isHealthy: false,
        details: {
          authentication: false,
          apiConnectivity: false
        },
        errorMessage: KioskSessionTransformer.transformApiError(error)
      };
    }
  }

  // enrichSessionsWithLocationHierarchy removed — sessions already have location_name from OCPI API

  /**
   * Groups sessions by location for UI display
   * Groups by OCPI location_name from session data
   * Deduplicates sessions to ensure each session appears in only one location group
   */
  private groupSessionsByLocation(sessions: KioskSessionGridRow[]): Map<string, KioskSessionGridRow[]> {
    const grouped = new Map<string, KioskSessionGridRow[]>();
    const seenSessionIds = new Set<string>();

    sessions.forEach(session => {
      if (seenSessionIds.has(session.session_id)) return;
      seenSessionIds.add(session.session_id);

      const locationKey = session.location_name || 'Unknown Location';

      if (!grouped.has(locationKey)) {
        grouped.set(locationKey, []);
      }

      grouped.get(locationKey)!.push(session);
    });

    // Sort each group by start time
    grouped.forEach((sessionGroup, location) => {
      sessionGroup.sort((a, b) => b.start_time.getTime() - a.start_time.getTime());
      grouped.set(location, sessionGroup);
    });

    return grouped;
  }

  /**
   * Creates hierarchical structure for TanStack Table expansion
   * Groups sessions by OCPI location_name from session data
   * Deduplicates sessions to ensure each session appears in only one location group
   */
  private createHierarchicalStructure(
    sessions: KioskSessionGridRow[]
  ): HierarchicalSessionRow[] {
    // Group sessions by location_name
    const locationGroups = new Map<string, KioskSessionGridRow[]>();
    const seenSessionIds = new Set<string>();

    sessions.forEach(session => {
      if (seenSessionIds.has(session.session_id)) return;
      seenSessionIds.add(session.session_id);

      const locationName = session.location_name || 'Unknown Location';

      if (!locationGroups.has(locationName)) {
        locationGroups.set(locationName, []);
      }

      locationGroups.get(locationName)!.push(session);
    });

    // Create hierarchical rows
    const hierarchicalRows: HierarchicalSessionRow[] = [];

    locationGroups.forEach((sessionsList, locationName) => {
      const totalEnergy = sessionsList.reduce((sum, s) => sum + (s.total_energy || 0), 0);
      const totalRevenue = sessionsList.reduce((sum, s) => sum + (s.settlement_value || 0), 0);
      const totalSettlement = sessionsList.reduce((sum, s) => sum + (s.settAmount || 0), 0);

      sessionsList.sort((a, b) => b.start_time.getTime() - a.start_time.getTime());

      const parentRow: LocationGroupRow = {
        id: `location-${locationName}`,
        type: 'location',
        locationId: locationName,
        locationName: locationName,
        sessionCount: sessionsList.length,
        totalEnergy,
        totalRevenue,
        totalSettlement,
        subRows: sessionsList
      };

      hierarchicalRows.push(parentRow);
    });

    // Sort parent rows by location name
    hierarchicalRows.sort((a, b) => {
      const aRow = a as LocationGroupRow;
      const bRow = b as LocationGroupRow;
      return aRow.locationName.localeCompare(bRow.locationName);
    });

    return hierarchicalRows;
  }

  /**
   * Fetches one export page without the per-page count call, retrying
   * transient failures (e.g. 504 gateway timeouts) with backoff.
   */
  private async fetchExportPage(
    filters: KioskSessionFilters,
    maxAttempts: number = 2
  ): Promise<{ data: KioskSessionGridRow[]; rawCount: number; rawOldest: number; hasError: boolean; errorMessage?: string }> {
    let lastError: unknown;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        // The repository pre-filters by last4Digits, which would shrink the row count and make a
        // full page look like the end of a window. Fetch unfiltered here; the same filter is
        // applied below by applyClientSideFilters.
        const response = await this.repository.getSessionsList({ ...filters, last4Digits: undefined });
        let data = KioskSessionTransformer.transformToGridRows(response);
        const rawCount = data.length;
        const rawOldest = data.reduce((min, r) => Math.min(min, r.start_time.getTime()), Infinity);
        data = KioskSessionTransformer.applyClientSideFilters(data, filters);
        return { data, rawCount, rawOldest, hasError: false };
      } catch (error) {
        lastError = error;
        if (attempt < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
        }
      }
    }
    return {
      data: [],
      rawCount: 0,
      rawOldest: Infinity,
      hasError: true,
      errorMessage: KioskSessionTransformer.transformApiError(lastError)
    };
  }

  /**
   * Gets ALL sessions for export by fetching multiple pages if needed
   * Uses cursor-based pagination to iterate through all results
   * @param filters - Base filters (date range, location, etc.)
   * @param maxSessionsPerPage - Maximum sessions to fetch per page (default 250; 1000 caused gateway timeouts)
   * @param onProgress - Optional callback for progress updates
   */
  async getAllSessionsForExport(
    filters: KioskSessionFilters,
    maxSessionsPerPage: number = 250,
    onProgress?: (current: number, total: number) => void
  ): Promise<{
    data: KioskSessionGridRow[];
    totalCount: number;
    hasError: boolean;
    errorMessage?: string;
  }> {
    try {
      console.log('📥 Starting export - fetching all sessions...');

      // Check authentication first
      const isAuthenticated = await this.repository.isAuthenticated();
      if (!isAuthenticated) {
        return {
          data: [],
          totalCount: 0,
          hasError: true,
          errorMessage: 'Authentication required. Please log in to export sessions.'
        };
      }

      let estimatedTotal = 0;
      try {
        estimatedTotal = await this.repository.getSessionsCount(filters);
      } catch {
        estimatedTotal = 0;
      }
      const DAY_MS = 24 * 60 * 60 * 1000;
      const MIN_WINDOW_DAYS = 1;
      const rangeStart = new Date(filters.dateFrom).getTime();
      const MIN_PAGE_SIZE = 50;
      let windowDays = 30;
      let pageSize = maxSessionsPerPage;
      let cursor = new Date(filters.dateTo).getTime();

      if (Number.isNaN(rangeStart) || Number.isNaN(cursor)) {
        return {
          data: [],
          totalCount: 0,
          hasError: true,
          errorMessage: 'Invalid date range for export.'
        };
      }

      const allSessions: KioskSessionGridRow[] = [];
      const seenIds = new Set<string>();

      while (cursor > rangeStart) {
        const windowStart = Math.max(rangeStart, cursor - windowDays * DAY_MS);

        console.log(`📄 Fetching ${new Date(windowStart).toISOString()} → ${new Date(cursor).toISOString()} (window ${windowDays}d)...`);

        const pageResult = await this.fetchExportPage({
          ...filters,
          dateFrom: new Date(windowStart).toISOString(),
          dateTo: new Date(cursor).toISOString(),
          limit: pageSize
        });

        if (pageResult.hasError) {
          if (windowDays > MIN_WINDOW_DAYS || pageSize > MIN_PAGE_SIZE) {
            // A timeout means the query is too heavy: make both the time slice and the row count smaller
            windowDays = Math.max(MIN_WINDOW_DAYS, Math.floor(windowDays / 2));
            pageSize = Math.max(MIN_PAGE_SIZE, Math.floor(pageSize / 2));
            console.warn(`⚠️ Page failed, shrinking to ${windowDays}d window / ${pageSize} rows and retrying`);
            continue;
          }
          console.error('❌ Error fetching export page:', pageResult.errorMessage);
          return {
            data: allSessions, // Return what we have so far
            totalCount: allSessions.length,
            hasError: true,
            errorMessage: `Failed to fetch all sessions. Retrieved ${allSessions.length}${estimatedTotal ? `/${estimatedTotal}` : ''}. Error: ${pageResult.errorMessage}`
          };
        }

        let added = 0;
        for (const row of pageResult.data) {
          if (!seenIds.has(row.session_id)) {
            seenIds.add(row.session_id);
            allSessions.push(row);
            added++;
          }
        }
        // Raw (pre client-filter) rows decide the cursor, so filtering can't skip data
        const oldest = pageResult.rawOldest;

        if (onProgress) {
          onProgress(allSessions.length, Math.max(estimatedTotal, allSessions.length));
        }

        const windowFull = pageResult.rawCount >= pageSize;
        if (windowFull && Number.isFinite(oldest) && oldest > windowStart) {
          cursor = added > 0 || oldest < cursor ? oldest : cursor - 1;
        } else {
          // Window exhausted: move to the next (older) window
          cursor = windowStart;
        }
      }

      return {
        data: allSessions,
        totalCount: allSessions.length,
        hasError: false
      };

    } catch (error) {
      console.error('GetKioskSessionsUseCase.getAllSessionsForExport failed:', error);

      return {
        data: [],
        totalCount: 0,
        hasError: true,
        errorMessage: KioskSessionTransformer.transformApiError(error)
      };
    }
  }

  /**
   * Gets ALL sessions grouped by location for export
   * Fetches all data across multiple pages if needed
   */
  async getAllSessionsGroupedForExport(
    filters: KioskSessionFilters,
    maxSessionsPerPage: number = 250,
    onProgress?: (current: number, total: number) => void
  ): Promise<{
    hierarchicalData: HierarchicalSessionRow[];
    totalCount: number;
    hasError: boolean;
    errorMessage?: string;
  }> {
    try {
      // Use the getAllSessionsForExport method to get all sessions
      const allSessionsResult = await this.getAllSessionsForExport(
        { ...filters, locationId: undefined, groupByLocation: true },
        maxSessionsPerPage,
        onProgress
      );

      if (allSessionsResult.hasError) {
        return {
          hierarchicalData: [],
          totalCount: 0,
          hasError: true,
          errorMessage: allSessionsResult.errorMessage
        };
      }

      // Group sessions by OCPI location_name
      const hierarchical = this.createHierarchicalStructure(allSessionsResult.data);

      return {
        hierarchicalData: hierarchical,
        totalCount: allSessionsResult.totalCount,
        hasError: false
      };

    } catch (error) {
      console.error('GetKioskSessionsUseCase.getAllSessionsGroupedForExport failed:', error);

      return {
        hierarchicalData: [],
        totalCount: 0,
        hasError: true,
        errorMessage: KioskSessionTransformer.transformApiError(error)
      };
    }
  }

  /**
   * Creates filters from date range with additional parameters
   * Utility method for common filter creation scenarios
   */
  createFiltersFromDateRange(
    startDate: Date,
    endDate: Date,
    additionalOptions?: {
      locationId?: string;
      last4Digits?: string;
      groupByLocation?: boolean;
    }
  ): KioskSessionFilters {
    // Convert Date objects to DD.MM.YYYY format
    const formatDate = (date: Date): string => {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear().toString();
      return `${day}.${month}.${year}`;
    };

    return {
      dateFrom: formatDate(startDate),
      dateTo: formatDate(endDate),
      locationId: additionalOptions?.locationId,
      last4Digits: additionalOptions?.last4Digits,
      groupByLocation: additionalOptions?.groupByLocation || false
    };
  }
}